/**
 * Tax Question API service (S1-P2 — versioned question-set / form-template config).
 *
 * Promise-based wrapper around the real backend endpoints under
 * `api/v1/TaxQuestion`. Mirrors the axios/base-URL/cookie-auth + ResponseModel
 * `unwrap`/`toError` pattern of `services/taxConfigApi.js`. All responses are
 * wrapped in the standard ResponseModel `{ message, status, data }`; helpers
 * below unwrap `.data` and throw on a non-success `status`
 * (APIStatus.Successful === 0) so callers can `await` the inner payload directly
 * and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs.
 *
 * @typedef {Object} TaxQuestionVersionDto
 * @property {number} questionVersionID @property {number} taxConfigVersionID @property {string} versionCode
 * @property {string} description @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionItemDto
 * @property {number} questionItemID @property {number} questionVersionID @property {string} itemKind
 * @property {?string} ruleCode @property {?string} fieldKey @property {string} label @property {?string} helpText
 * @property {?string} questionTextTh @property {?string} systemPrompt
 * @property {?string} answerSystemPrompt @property {?string} questionSystemPrompt
 * @property {?string} answerGuidanceTh @property {boolean} showRuleCap
 * @property {string} inputType @property {?string} answerType @property {?string} controlType
 * @property {?Object[]} options @property {?string[]} answerOptions @property {?string[]} pndCodes
 * @property {string} visibleWhen @property {boolean} isRequired @property {?string} groupCode
 * @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionStepDto
 * @property {number} questionStepID @property {string} stepCode @property {string} label @property {?string} description
 * @property {?string} systemPrompt @property {?string} answerSystemPrompt @property {?string} questionSystemPrompt @property {?string[]} knowledgeIds @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionStepRequest
 * @property {string} stepCode @property {string} label @property {?string} description
 * @property {?string} systemPrompt @property {?string} answerSystemPrompt @property {?string} questionSystemPrompt @property {?string[]} knowledgeIds @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionGroupDto
 * @property {string} groupCode @property {string} groupLabel @property {?string} systemPrompt @property {?string} answerSystemPrompt @property {?string} questionSystemPrompt @property {?string[]} knowledgeIds
 * @property {number} sortOrder @property {TaxQuestionItemDto[]} items
 *
 * @typedef {Object} TaxQuestionFullDto
 * @property {TaxQuestionVersionDto} version @property {TaxQuestionStepDto[]} steps
 * @property {TaxQuestionGroupDto[]} groups @property {string[]} warnings
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) {
      return body.data;
    }
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.data = body.data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function patch(url, payload) {
  try {
    return unwrap(await http.patch(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxQuestion';

export const taxQuestionApi = {
  // ── Question Versions ────────────────────────────────────────────────
  /**
   * List question versions, optionally filtered by the bound config version.
   * @param {?number} configVersionId
   * @param {boolean} includeInactive
   * @returns {Promise<TaxQuestionVersionDto[]>}
   */
  getVersions(configVersionId = null, includeInactive = true) {
    let url = `${ROOT}/versions?includeInactive=${includeInactive}`;
    if (configVersionId != null) url += `&configVersionId=${configVersionId}`;
    return get(url);
  },
  /** @returns {Promise<TaxQuestionVersionDto>} */
  getVersion(id) {
    return get(`${ROOT}/versions/${id}`);
  },
  /** @returns {Promise<TaxQuestionFullDto>} version + items grouped — load builder */
  getFull(id, includeInactive = true) {
    return get(`${ROOT}/versions/${id}/full?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxQuestionVersionDto>} */
  createVersion(body) {
    return post(`${ROOT}/versions`, body);
  },
  /** @returns {Promise<TaxQuestionVersionDto>} */
  updateVersion(id, body) {
    return put(`${ROOT}/versions/${id}`, body);
  },
  /** @returns {Promise<boolean>} (siblings auto-deactivated server-side) */
  setVersionActive(id, isActive) {
    return patch(`${ROOT}/versions/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} (items cascade-deleted server-side) */
  deleteVersion(id) {
    return del(`${ROOT}/versions/${id}`);
  },

  // ── Question Items ───────────────────────────────────────────────────
  /** @returns {Promise<TaxQuestionItemDto[]>} */
  getItems(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/items?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxQuestionItemDto>} */
  getItem(itemId) {
    return get(`${ROOT}/items/${itemId}`);
  },
  /** @returns {Promise<TaxQuestionItemDto>} */
  createItem(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/items`, body);
  },
  /** @returns {Promise<TaxQuestionItemDto>} */
  updateItem(itemId, body) {
    return put(`${ROOT}/items/${itemId}`, body);
  },
  /** @returns {Promise<boolean>} */
  setItemActive(itemId, isActive) {
    return patch(`${ROOT}/items/${itemId}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteItem(itemId) {
    return del(`${ROOT}/items/${itemId}`);
  },
  /**
   * Replace the whole step + item set of a version (builder "Save") — transactional.
   * Accepts either a plain items array (legacy `{ items }` body) or an object
   * `{ steps, items }` (opening-question augment). When `steps` is omitted the
   * server leaves existing step rows untouched (backward-compatible).
   * @param {number} versionId
   * @param {Object[]|{steps?:TaxQuestionStepRequest[],items:Object[]}} payload
   *   array of CreateTaxQuestionItemRequest-shaped objects, or `{ steps, items }`
   * @returns {Promise<TaxQuestionItemDto[]>}
   */
  bulkSaveItems(versionId, payload) {
    let body;
    if (Array.isArray(payload)) {
      body = { items: payload };
    } else {
      const p = payload || {};
      body = { items: Array.isArray(p.items) ? p.items : [] };
      if (p.steps !== undefined) body.steps = Array.isArray(p.steps) ? p.steps : [];
    }
    return put(`${ROOT}/versions/${versionId}/items/bulk`, body);
  },
};

export default taxQuestionApi;
